import { baseApi } from './baseApi';

// Question interface
export interface Question {
  id?: string;
  question: string;
  questionType: 'text' | 'multiple_choice' | 'checkbox' | 'dropdown';
  options?: string[];
  required: boolean;
  order: number;
  createdAt?: string;
  updatedAt?: string;
}

// API Response types
export interface QuestionsResponse {
  success: boolean;
  data: Question[];
  total?: number;
  page?: number;
  limit?: number;
}

export interface QuestionResponse {
  success: boolean;
  data: Question;
  message?: string;
}

// Query parameters
export interface QuestionQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  questionType?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export const questionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    // ⭐ Get all questions with pagination/filter
    getQuestions: builder.query<QuestionsResponse, Partial<QuestionQueryParams>>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams({
          page: params.page?.toString() || '1',
          limit: params.limit?.toString() || '50',
          sortBy: params.sortBy || 'order_index',
          sortOrder: params.sortOrder || 'ASC',
        });

        if (params.search) queryParams.append('search', params.search);
        if (params.questionType) queryParams.append('questionType', params.questionType);

        return {
          url: `/question/list?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Question'],
    }),

    // ⭐ Get one question
    getQuestionById: builder.query<QuestionResponse, string>({
      query: (id) => ({
        url: `/questions/${id}`,
        method: 'GET',
      }),
      providesTags: ['Question'],
    }),

    // ⭐ Create a question
    createQuestion: builder.mutation<QuestionResponse, Partial<Question>>({
      query: (data) => ({
        url: `/questions`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Question'],
    }),

    // ⭐ Update a question
    updateQuestion: builder.mutation<
      QuestionResponse,
      { id: string; updates: Partial<Question> }
    >({
      query: ({ id, updates }) => ({
        url: `/questions/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['Question'],
    }),

    // ⭐ Delete a question
    deleteQuestion: builder.mutation<
      { success: boolean; message: string },
      string
    >({
      query: (id) => ({
        url: `/questions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Question'],
    }),

    // ⭐ Bulk question order update
    updateQuestionOrder: builder.mutation<
      { success: boolean },
      { questions: Array<{ id: string; order: number }> }
    >({
      query: ({ questions }) => ({
        url: `/questions/update-order`,
        method: 'PATCH',
        body: { questions },
      }),
      invalidatesTags: ['Question'],
    }),

  }),
  overrideExisting: false,
});

// Hooks
export const {
  useGetQuestionsQuery,
  useGetQuestionByIdQuery,
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  useUpdateQuestionOrderMutation,
} = questionApi;
